import React from "react";
import { getCompetitionVerdict, getCompetitionReason, COMPETITION_STYLES, formatStars } from "../lib/reasoning";

/**
 * Data-grounded Competition verdict that OVERRIDES the optimistic "BUILD THE MVP"
 * recommendation. Renders the GREEN/YELLOW/RED/UNVERIFIED badge, the established
 * competitors as clickable links (with ⭐ stars / "funded" tag), and the original
 * P2P score struck-through whenever the verdict downgraded it.
 */
export const CompetitionVerdict = ({ opportunity, compact = false, heading = "Competition check" }) => {
  const v = getCompetitionVerdict(opportunity);
  if (!v) return null;
  const s = COMPETITION_STYLES[v.color] || COMPETITION_STYLES.grey;
  const reason = getCompetitionReason(v);
  const max = compact ? 4 : 8;
  return (
    <div
      className={`mt-2 px-2.5 py-2 rounded-lg border ${s.bg} ${s.border} ${s.text}`}
      data-testid={`competition-verdict-${opportunity.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-70">{heading}</div>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full ${s.badge} text-white text-[10px] uppercase tracking-[0.14em] font-bold`}
          data-testid={`competition-badge-${opportunity.id}`}
        >
          {v.verdict}
        </span>
      </div>

      <div className="text-[11px] font-semibold leading-snug" data-testid={`competition-label-${opportunity.id}`}>
        {v.downgraded && v.originalScore != null && (
          <span className="mr-1 opacity-80">
            <span className="line-through opacity-60" data-testid={`competition-strikethrough-${opportunity.id}`}>
              BUILD · {v.originalScore}
            </span>{" "}
            →{" "}
          </span>
        )}
        {v.label}
      </div>

      {reason && (
        <div
          className="text-[11px] mt-1 leading-snug opacity-90"
          data-testid={`competition-reason-${opportunity.id}`}
        >
          {reason}
        </div>
      )}

      {v.competitors.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`competition-competitors-${opportunity.id}`}>
          {v.competitors.slice(0, max).map((c, i) => {
            const stars = formatStars(c.stars);
            return (
              <a
                key={`${c.url}-${i}`}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={c.url}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/80 border border-current/20 text-[10px] font-semibold hover:underline"
                data-testid={`competition-comp-${opportunity.id}-${i}`}
              >
                {c.name}
                {stars && <span className="opacity-60">★{stars}</span>}
                {c.funded === "yes" && <span className="opacity-60">· funded</span>}
              </a>
            );
          })}
        </div>
      )}

      {v.searchStatus !== "ok" && (
        <div className="text-[10px] mt-1 opacity-80" data-testid={`competition-warning-${opportunity.id}`}>
          ⚠️ Search {v.searchStatus} — verdict cannot be trusted.
        </div>
      )}

      {v.verdict === "GREEN" && v.saasCheckIncomplete && (
        <div className="text-[10px] mt-1 opacity-80" data-testid={`competition-saas-incomplete-${opportunity.id}`}>
          ⚠️ Funded-SaaS check incomplete — verify funded competitors manually.
        </div>
      )}
    </div>
  );
};

export default CompetitionVerdict;
